<mj-page-layout>
    @switch (view) {
        @case ('detail') {
            <mj-page-body>
                @if (recordID) {
                    <ra-job-detail [JobID]="recordID" [Recommended]="DetailIsRecommended" (SeeSimilar)="OnSeeSimilar()" (BackToHome)="OnBackToHome()" />
                } @else {
                    <!-- No record named: the gone-listing panel (P/proto-states.js:121). -->
                    <agu-state-panel [Icon]="GoneCopy.Icon" [Title]="GoneCopy.Title" [Body]="GoneCopy.Body" PrimaryLabel="See similar" SecondaryLabel="Back to Home" (Primary)="OnSeeSimilar()" (Secondary)="OnBackToHome()" />
                }
            </mj-page-body>
        }
        @default {
            <mj-page-body>
                <agu-page-band
                    Eyebrow="Jobs"
                    Title="Job Finder"
                    Sub="Find faculty and postdoc openings, research and industry roles, policy positions, and other opportunities across the Earth and space sciences."
                    [Accent]="Accent"
                />

                @if (available === false) {
                    <agu-state-panel
                        Icon="clock"
                        Title="Job matching isn't switched on yet"
                        Body="The job-posting feed this module matches against is still being set up. Once it's live, roles matched to your profile will appear here — nothing else about your account needs to change."
                    />
                } @else {
                    <agu-search-hero
                        [Eyebrow]="HeroEyebrow"
                        Placeholder="Describe the role you're looking for, search by title, employer, skill, or location, or paste a job description or CV summary."
                        SubmitLabel="Search"
                        BusyLabel="Searching…"
                        AttachLabel="Attach your CV"
                        AttachHint="PDF, DOCX or TXT"
                        AttachedLabel="CV attached"
                        [Applied]="Applied"
                        [Accent]="Accent"
                        [Attachment]="attachment"
                        [Suggestions]="cx ? [] : TryChips"
                        [Busy]="loading"
                        [Query]="state.query"
                        (QueryChange)="OnQueryChange($event)"
                        (Submitted)="OnSubmitted($event)"
                        (FileSelected)="OnFileSelected($event)"
                        (AttachmentRemoved)="OnAttachmentRemoved()"
                        (SuggestionSelected)="OnSuggestionSelected($event)"
                    >
                        <!-- PJ:1402-1405: the try row is replaced by what the path applied. No "Ranked by your fit" chip (no per-posting skill data on the wire). -->
                        @if (cx) {
                            <div TryRow class="ra-cxrow">
                                <span class="ra-cxfrom">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 19 9 12l3.5 4L20 5"/><path d="M15 5h5v5"/></svg>From Career Explorer
                                </span>
                                @for (chip of CxChips; track chip) { <span class="ra-cxchip">{{ chip }}</span> }
                            </div>
                        }
                    </agu-search-hero>
                    <!-- The landing strip (P/Member_Hub.html:1383-1401): landing state only; fades back in when results give way. -->
                    @if (HeroStrip; as ad) { <agu-ad-slot [Placement]="ad" [Reveal]="RevealStamp > 0" /> }

                    @if (cvDocument && !attachment) {
                        <button type="button" class="ra-jobs__use-cv" (click)="OnUseCV()">
                            <i class="fa-solid fa-file-lines" aria-hidden="true"></i>
                            Use my CV on file ({{ cvDocument.fileName }})
                        </button>
                    }

                    <agu-tab-strip
                        class="ra-viewtabs"
                        [Tabs]="Tabs"
                        [Active]="state.tab"
                        [PanelId]="ResultsPanelId"
                        (ActiveChange)="OnTabChange($event)"
                    />
                    <!-- One caption per tab, verbatim from the mockup's VIEWNOTE (jobs.html:843-846); the <b> takes the module accent. -->
                    @if (state.tab === 'recommended') {
                        <p class="ra-viewnote" aguFade>Roles matched to your <b>publication history</b>, seniority and stated career interests.</p>
                    } @else {
                        <p class="ra-viewnote" aguFade>Every open role we track, filter by location, sector, salary or science area.</p>
                    }

                    <div class="ra-resultwrap" [class.ra-resultwrap--full]="state.tab !== 'all'">
                        <!-- On a phone the rail is projected into the bottom filter sheet (P/Member_Hub.html:603-620, 896-911); one template, two hosts. -->
                        @if (state.tab === 'all') {
                            @if (Phone.IsPhone) {
                                <agu-filter-sheet [Open]="Phone.FiltersOpen" [ScrollToGroup]="Phone.FilterGroup" (Closed)="Phone.CloseFilters()" (ShowResults)="Phone.CloseFilters()">
                                    <ng-container *ngTemplateOutlet="filterRail" />
                                </agu-filter-sheet>
                            } @else {
                                <ng-container *ngTemplateOutlet="filterRail" />
                            }
                        }
                        <ng-template #filterRail>
                            <agu-filter-rail [ActiveCount]="ActiveFilterCount" (ClearAll)="OnClearFilters()">
                                <ra-job-filters
                                    [Filters]="state.filters"
                                    (FiltersChange)="OnFiltersChange($event)"
                                    [Facets]="facets"
                                    [Region]="region"
                                    (RegionChange)="OnRegionChange($event)"
                                    [WorkLocation]="workLocation"
                                    (WorkLocationChange)="OnWorkLocationChange($event)"
                                    [PostedThisWeek]="postedThisWeek"
                                    (PostedThisWeekChange)="OnPostedThisWeekChange($event)"
                                    [ShowSavedOnly]="showSavedOnly"
                                    (ShowSavedOnlyChange)="showSavedOnly = $event"
                                    [RecommendedOnly]="state.filters.recommendedOnly === true"
                                    (RecommendedOnlyChange)="OnRecommendedOnlyChange($event)"
                                />
                                <!-- The employer spotlight, appended to the rail (P/Member_Hub.html:1534-1544). -->
                                @if (RailSpot; as ad) { <agu-ad-slot [Placement]="ad" /> }
                            </agu-filter-rail>
                        </ng-template>

                        <div class="ra-resultcol">
                            <!-- The mockup's Recommended section holds only its grid (and pinned section): no sort chip, no filter pills (P-G11/P-C9/P-J8). -->
                            @if (state.tab === 'all') {
                                <!-- The phone's quick-filter row: Filters [count], the sort, the first four groups (P/Member_Hub.html:915-951). -->
                                @if (Phone.IsPhone) {
                                    <agu-mobile-chips
                                        [FilterCount]="ActiveFilterCount"
                                        [SortOptions]="PhoneSortOptions"
                                        [Sort]="state.sort"
                                        [Groups]="FilterGroups"
                                        [ActiveGroups]="ActiveFilterGroups"
                                        (OpenFilters)="Phone.OpenFilters($event)"
                                        (SortChange)="OnSortChange($event)"
                                    />
                                }
                                <ra-finder-toolbar
                                    [Sort]="state.sort"
                                    [SortOptions]="SortOptions"
                                    [Count]="ResultCount"
                                    (SortChange)="OnSortChange($event)"
                                />
                                <ra-active-pills [Pills]="FilterPills" (Remove)="OnRemoveFilterGroup($event)" />
                            }

                            <div class="ra-finder__results" [id]="ResultsPanelId" role="tabpanel"
                [aguReveal]="RevealDirectionValue" [RevealKey]="RevealStamp" RevealItems=":scope .ra-grid > *, :scope .ra-list > *">
                                @if (state.tab === 'recommended' && PinnedItems.length > 0) {
                                    <section class="ra-finder__section" aria-labelledby="ra-jobs-pinned-h">
                                        <h3 id="ra-jobs-pinned-h">Pinned by you</h3>
                                        <div class="ra-list">
                                            @for (item of PinnedItems; track item.jobId) {
                                                <ra-job-card
                                                    [aguRise]="$index" [RiseDistance]="6" [RiseKey]="RiseStamp"
                                                    [Job]="item"
                                                    [Recommended]="true"
                                                    [ShowPin]="true"
                                                    (Save)="OnSave(item, $event)"
                                                    (Pin)="OnPin(item, $event)"
                                                    (Dismiss)="OnDismiss(item)"
                                                    (Open)="OnOpen(item)"
                                                />
                                            }
                                        </div>
                                    </section>
                                }

                                <!-- Product states (PD §5; P/proto-states.js:97-98, 115, 129): 4 skeleton cards, the error panel, the empties. -->
                                @if (loading) {
                                    <agu-skeleton [Count]="4" />
                                } @else if (error) {
                                    <agu-state-panel
                                        Variant="error"
                                        [Icon]="ErrorCopy.Icon"
                                        [Title]="ErrorCopy.Title"
                                        [Body]="ErrorCopy.Body"
                                        PrimaryLabel="Try again"
                                        [SecondaryLabel]="failures.SupportHref ? 'Contact support' : null"
                                        [SecondaryHref]="failures.SupportHref"
                                        [Foot]="failures.Foot"
                                        [Note]="failures.Note"
                                        (Primary)="Retry()"
                                    />
                                } @else if (IsPreSearch) {
                                    <agu-state-panel
                                        Icon="search"
                                        Title="Search jobs to get started"
                                        Body="Describe the role you're after, try one of the suggestions above, or open Recommended for openings matched to your profile."
                                    />
                                } @else if (IsEmpty) {
                                    @if (IsUnrecognized) {
                                        <agu-state-panel
                                            Icon="search"
                                            Title="We couldn't understand that search."
                                            Body="Try a topic, role or place — for example “hydrology jobs in Colorado”."
                                        />
                                    } @else if (ShowNothingToMatchYet) {
                                        <agu-state-panel
                                            Icon="spark"
                                            Title="Nothing to match yet"
                                            Body="Recommendations use your AGU publication record and career interests. Search with a few sentences about the role you want, or upload a CV in Career Explorer."
                                        />
                                    } @else if (state.tab === 'recommended') {
                                        <agu-state-panel
                                            [Icon]="RecommendedEmptyCopy.Icon"
                                            [Title]="RecommendedEmptyCopy.Title"
                                            [Body]="RecommendedEmptyCopy.Body"
                                            [PrimaryLabel]="BrowseAll"
                                            SecondaryLabel="Widen research areas"
                                            (Primary)="SetTab('all')"
                                            (Secondary)="OnWidenResearchAreas()"
                                        />
                                    } @else {
                                        <agu-state-panel
                                            [Icon]="AllEmptyCopy.Icon"
                                            [Title]="AllEmptyCopy.Title"
                                            [Body]="AllEmptyCopy.Body"
                                            SecondaryLabel="Widen research areas"
                                            (Secondary)="OnWidenResearchAreas()"
                                        />
                                    }
                                } @else {
                                    <div class="ra-list">
                                        @for (item of DisplayItems; track item.jobId) {
                                            <ra-job-card
                                                [aguRise]="$index" [RiseDistance]="6" [RiseKey]="RiseStamp"
                                                [Job]="item"
                                                [Recommended]="state.tab === 'recommended'"
                                                [ShowPin]="true"
                                                (Save)="OnSave(item, $event)"
                                                (Pin)="OnPin(item, $event)"
                                                (Dismiss)="OnDismiss(item)"
                                                (Open)="OnOpen(item)"
                                            />
                                            <!-- The featured job: position 3, after the second organic item (P/Member_Hub.html:1570-1578). -->
                                            @if ($index === 1) {
                                                @if (InListJob; as ad) { <agu-ad-slot [Placement]="ad" /> }
                                            }
                                        }
                                    </div>

                                    @if (state.tab === 'all') {
                                        <agu-pagination [Page]="state.page" [PageSize]="state.pageSize" [Total]="PagerTotal" (PageChange)="OnPageChange($event)" />
                                    }
                                }
                            </div>

                            @if (LastDismissed$ | async; as dismissed) {
                                <mj-alert aguFade Variant="info" Size="sm" Role="status">
                                    Hidden: <b>{{ dismissed.title }}</b>
                                    <button type="button" mjButton variant="flat" class="agu-hit-24" actions (click)="Undo()">Undo</button>
                                </mj-alert>
                            }
                        </div>
                    </div>
                }
            </mj-page-body>
        }
    }
</mj-page-layout>
